import i18nFactory from '@yandex-int/i18n';
import { findLastIndex } from 'lodash';

import {
    ClicksByPriceData,
    Phrase,
    TrafficVolumeDataItem
} from '../../typings/domain';
import {
    GdShowConditionAutobudgetPriority,
    GdShowConditionPrimaryStatus
} from '../../typings/graphql-grid';
import { SearchAndReplaceFormValue } from '../../typings/searchAndReplace';
import { precisionFloor } from '../FormatHelpers/FormatHelpers';

import * as keyset from './PhrasesHelpers.i18n';

const i18n = i18nFactory(keyset);

export const autobudgetPriorityDictionary: Record<
    GdShowConditionAutobudgetPriority,
    string
> = {
    [GdShowConditionAutobudgetPriority.LOW]: i18n('low-priority'),
    [GdShowConditionAutobudgetPriority.MEDIUM]: i18n('medium-priority'),
    [GdShowConditionAutobudgetPriority.HIGH]: i18n('high-priority')
};

export const mapAutobudgetPriorityToString = (
    autobudgetPriority: GdShowConditionAutobudgetPriority
): string => autobudgetPriorityDictionary[autobudgetPriority];

const phraseStatusInfoDictionary: Record<
    GdShowConditionPrimaryStatus,
    string
> = {
    [GdShowConditionPrimaryStatus.ACTIVE]: i18n('status-active'),
    [GdShowConditionPrimaryStatus.ARCHIVED]: i18n('status-archived'),
    [GdShowConditionPrimaryStatus.STOPPED]: i18n('status-stopped'),
    [GdShowConditionPrimaryStatus.MODERATION_REJECTED]: i18n(
        'status-reject-by-moderator'
    ),
    [GdShowConditionPrimaryStatus.DRAFT]: i18n('status-draft'),
    [GdShowConditionPrimaryStatus.ARCHIVED]: i18n('status-archived')
};

export function getPhraseStatusInfo(
    status: GdShowConditionPrimaryStatus
): string {
    return phraseStatusInfoDictionary[status];
}

export function getPhrasesDataForApiRequest(
    phrasesIds: Array<string>,
    allPhrasesData: Array<Phrase>
): Record<string, string> {
    const phrasesGroupsInfo = allPhrasesData.reduce((acc, phrase) => {
        acc[phrase.id] = phrase.groupId;

        return acc;
    }, {});

    return phrasesIds.reduce((acc, phraseId) => {
        const phraseGroupId = phrasesGroupsInfo[phraseId];

        if (!phraseGroupId) {
            return acc;
        }

        if (acc[phraseGroupId]) {
            acc[phraseGroupId].push(phraseId);
        } else {
            acc[phraseGroupId] = [phraseId];
        }

        return acc;
    }, {});
}

export function mapPhraseWithMinusWordsToString(
    phrase: string,
    minusWords: string[]
): string {
    const prefix = minusWords.length ? ' -' : '';
    const minusWordsString = `${prefix}${minusWords.join(' -')}`;
    return `${phrase}${minusWordsString}`;
}

export function mapStringToPhraseWithMinusWords(
    input: string
): { phrase: string; minusWords: string[] } {
    let onMinusWords = false;
    return input
        .split(' ')
        .map(el => el.trim())
        .filter(el => el)
        .reduce(
            (acc: { phrase: string; minusWords: string[] }, el: string) => {
                if (el.startsWith('-') || onMinusWords) {
                    onMinusWords = true;
                    const toPush = el.startsWith('-') ? el.slice(1) : el;
                    if (toPush) {
                        acc.minusWords.push(toPush);
                    }
                } else {
                    acc.phrase = `${acc.phrase}${acc.phrase ? ' ' : ''}${el}`;
                }

                return acc;
            },
            { phrase: '', minusWords: [] }
        );
}

export const isPhraseEditable = (phrase: Phrase): boolean => {
    return phrase.access.canEdit;
};

export function getTrafaretsAuctionDataByParamValue(
    paramName: 'bid' | 'trafficVolume',
    value: number,
    trafficVolumeData: Array<TrafficVolumeDataItem>
): TrafficVolumeDataItem | undefined {
    // ищем нижнюю границу
    const leftBorderValueIndex = findLastIndex(
        trafficVolumeData,
        item => item[paramName] <= value
    );

    const left = trafficVolumeData[leftBorderValueIndex];

    if (left) {
        const right = trafficVolumeData[leftBorderValueIndex + 1];

        if (right) {
            // вычисляем промежуточные значения показателей интерполяцией
            return {
                trafficVolume: precisionFloor(
                    getLinearInterpolation({
                        value: value,
                        x1: left[paramName],
                        x2: right[paramName],
                        y1: left.trafficVolume,
                        y2: right.trafficVolume
                    }),
                    2
                ),
                amnestyPrice: precisionFloor(
                    getLinearInterpolation({
                        value: value,
                        x1: left[paramName],
                        x2: right[paramName],
                        y1: left.amnestyPrice,
                        y2: right.amnestyPrice
                    }),
                    2
                ),
                bid: precisionFloor(
                    getLinearInterpolation({
                        value: value,
                        x1: left[paramName],
                        x2: right[paramName],
                        y1: left.bid,
                        y2: right.bid
                    }),
                    2
                )
            };
        } else {
            return {
                trafficVolume:
                    paramName === 'trafficVolume' ? value : left.trafficVolume,
                amnestyPrice: left.amnestyPrice,
                bid: paramName === 'bid' ? value : left.bid
            };
        }
    } else {
        return undefined;
    }
}

export function getContextCoverageByContextPrice(
    priceContext: number,
    contextCoverage: Array<ClicksByPriceData>,
    maxShows: number
): number | undefined {
    let coverage;
    const leftBorderValueIndex = findLastIndex(
        contextCoverage,
        item => item.price <= priceContext
    );

    const left = contextCoverage[leftBorderValueIndex];

    if (left) {
        const right = contextCoverage[leftBorderValueIndex + 1];

        if (right) {
            // вычисляем промежуточные значения показателей интерполяцией
            coverage = getLinearInterpolation({
                value: priceContext,
                x1: left.price,
                x2: right.price,
                y1: left.clicks,
                y2: right.clicks
            });
        } else {
            coverage = left.clicks;
        }

        // Преобразовываем в проценты относительно максимального охвата
        coverage = precisionFloor((coverage / maxShows) * 100, 2);
    }

    return coverage;
}

export type InterpolationPoints = {
    value: number;
    x1: number;
    x2: number;
    y1: number;
    y2: number;
};

export const getLinearInterpolation = ({
    value,
    x1,
    x2,
    y1,
    y2
}: InterpolationPoints): number => y1 + ((value - x1) * (y2 - y1)) / (x2 - x1);

export function validateSearchAndReplaceFormValue(
    value: SearchAndReplaceFormValue
): string | void {
    const { replaceValue } = value;

    if (replaceValue.includes(' -') || replaceValue.includes(',')) {
        return i18n('replace-validation-error-invalid-characters');
    }
}

const sphreadPatternRegexp = /\(([^()]+)\)/;

export const phrasesSphread = (input: string): string[] => {
    const result = [];

    let toProcess: string[] = [input];

    while (toProcess.length) {
        const item = toProcess.shift() as string;

        if (sphreadPatternRegexp.test(item)) {
            toProcess = [...toProcess, ...expandPattern(item)];
        } else {
            result.push(item);
        }
    }

    return result;
};

const expandPattern = (str: string): string[] => {
    const match = str.match(sphreadPatternRegexp);

    if (!match) {
        return [];
    }

    const wordsString = match[1];

    return wordsString
        .split('|')
        .map((el: string) => el.trim())
        .filter(Boolean)
        .map((el: string) => str.replace(sphreadPatternRegexp, el));
};
